/**
 * Visuals hook — AI background generation + saved visuals (Module 2 canvas).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/api';

export type VisualFormat = 'post_square' | 'post_portrait' | 'story';

interface BackgroundResult {
  dataUrl: string;
  format: VisualFormat;
  dimensions: { label: string; width: number; height: number };
}

/** A saved visual returned by GET /visuals (no heavy file data in the list). */
export interface SavedVisualSummary {
  id: string;
  kind: 'post' | 'story' | 'carousel' | 'thumbnail';
  /** First entry encodes the title + preview thumbnail. See SavedVisualHeader. */
  layers: SavedVisualHeader[];
  created_at: string;
  updated_at: string;
}

/** The first layer entry we store on save — a metadata header for the library list. */
export interface SavedVisualHeader {
  __meta: true;
  title: string;
  /** PNG data URL — small thumbnail (≤ 360px wide) used for the library grid. */
  thumbnail: string;
}

export function useVisuals() {
  const qc = useQueryClient();

  const generateBackground = useMutation({
    mutationFn: (input: { format: VisualFormat; prompt: string }) =>
      apiRequest<BackgroundResult>('/visuals/background', { method: 'POST', body: input }),
  });

  const saveVisual = useMutation({
    mutationFn: (input: { kind: string; layers: unknown[]; export_url?: string }) =>
      apiRequest<{ id: string }>('/visuals', { method: 'POST', body: input }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['visuals'] }),
  });

  const deleteVisual = useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ ok: boolean }>(`/visuals/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['visuals'] }),
  });

  return { generateBackground, saveVisual, deleteVisual };
}

/** Library list query — used by the /bibliotheque page. */
export function useVisualLibrary() {
  return useQuery<SavedVisualSummary[]>({
    queryKey: ['visuals'],
    queryFn: () => apiRequest<SavedVisualSummary[]>('/visuals'),
  });
}
